import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import {
  Card,
  CardBody,
  Col,
  Container,
  Input,
  Label,
  Row,
  Button,
  Table,
  Nav,
  NavItem,
  NavLink,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap";
import {
  getSalesInvoices,
  getSalesInvoiceForReturn,
  getInvoiceForPrint
} from "../../components/Api/GmsBilling";
import { getCustomers, searchCustomers } from "../../components/Api/GmsCustomer";
import { getBusinesses } from "../../components/Api/GmsBusiness";
import { getBranches } from "../../components/Api/GmsBranch";
import { getProducts } from "../../components/Api/GmsProduct";
import { getSpaServices } from "../../components/Api/GmsSpaService";
import { getSpaPackages } from "../../components/Api/GmsSpaPackage";
import { getSpaPackageSeries } from "../../components/Api/GmsSpaPackageSeries";
import { getEmployees } from "../../components/Api/GmsEmployee";
import { Eye, Receipt, RotateCcw, Printer, Search, FileText } from "lucide-react";
import { ClipLoader } from "react-spinners";
import Swal from "sweetalert2";
import {
  hasPagePermissionWithAdmin,
  hasEditOrHigherPermission,
  isUserAdmin
} from "../../components/Permission/PermissionService";
import Breadcrumb from "../../components/Common/Breadcrumb";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Pagination from "rc-pagination";
import 'rc-pagination/assets/index.css';
import html2pdf from "html2pdf.js";
import BASE_URL from "../../config";
import CustomerAdvancedSearchModal from "../../components/CustomerAdvancedSearchModal";
import CustomerProfileModal from "../../components/CustomerProfileModal";

// Custom styles for Select components
const selectStyles = {
  control: (provided, state) => ({
    ...provided,
    minHeight: '38px',
    borderColor: state.isFocused ? '#556ee6' : '#ced4da',
    boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(85, 110, 230, 0.25)' : 'none',
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 9999,
    maxHeight: 200,
  }),
  menuList: (provided) => ({
    ...provided,
    maxHeight: 180,
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? '#556ee6' : state.isFocused ? '#f8f9fa' : 'white',
    color: state.isSelected ? 'white' : '#495057',
    '&:hover': {
      backgroundColor: state.isSelected ? '#556ee6' : '#e9ecef',
    },
  }),
};

const emptyFilter = {
  Invoice_Id: null,
  Customer_Id: null,
  FromDate: null,
  ToDate: null,
  Invoice_Type: null,
  PaymentMethod: null,
};

export default function InvoiceListPage() {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [customerSearchOptions, setCustomerSearchOptions] = useState([]);
  const [customerSearchLoading, setCustomerSearchLoading] = useState(false);
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [selectedCustomerFilterOption, setSelectedCustomerFilterOption] = useState(null);
  const [showAdvancedSearchModal, setShowAdvancedSearchModal] = useState(false);

  // Get user session for branch filtering
  const getUserSession = () => {
    const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    return {
      branchId: authUser.branchId || user.user_Branch_Id || null,
    };
  };
  const session = useMemo(() => getUserSession(), []);
  const currencyCode = localStorage.getItem("currencyCode") || "AED";
  const [businesses, setBusinesses] = useState([]);
  const [branches, setBranches] = useState([]);
  const [products, setProducts] = useState([]);
  const [services, setServices] = useState([]);
  const [packages, setPackages] = useState([]);
  const [packageSeries, setPackageSeries] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [filter, setFilter] = useState(emptyFilter);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [hasAppliedFilter, setHasAppliedFilter] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [invoiceListType, setInvoiceListType] = useState('normal');
  const [showInvoicePrintModal, setShowInvoicePrintModal] = useState(false);
  const [invoicePrintData, setInvoicePrintData] = useState(null);
  const [printLoading, setPrintLoading] = useState(false);
  const [showCustomerProfile, setShowCustomerProfile] = useState(false);
  const [selectedCustomerId, setSelectedCustomerId] = useState(null);

  // Check permissions
  const permissions = JSON.parse(localStorage.getItem('permissions') || '[]');
  const isAdmin = isUserAdmin();
  const hasPermission = hasPagePermissionWithAdmin(permissions, '/GmsBilling'); // Use same permission as Billing
  const hasEditOrHigher = hasEditOrHigherPermission(permissions, '/GmsBilling');

  // Load initial data
  useEffect(() => {
    if (hasPermission) {
      loadInitialData();
      loadInvoices();
    } else {
      window.location.href = '/Dashboard';
    }
  }, [hasPermission]);

  // Reload invoices when filter is applied
  useEffect(() => {
    if (hasAppliedFilter) {
      loadInvoices();
      setHasAppliedFilter(false);
    }
  }, [hasAppliedFilter]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        loadCustomers(),
        loadBusinesses(),
        loadBranches(),
        loadProducts(),
        loadServices(),
        loadPackages(),
        loadPackageSeries(),
        loadEmployees(),
      ]);
    } catch (error) {
      console.error("Error loading initial data:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadCustomers = async () => {
    try {
      const data = await getCustomers({ status: true });
      setCustomers(data || []);
    } catch (error) {
      console.error("Error loading customers:", error);
    }
  };

  const loadBusinesses = async () => {
    try {
      const data = await getBusinesses({ business_Active: true });
      setBusinesses(data || []);
    } catch (error) {
      console.error("Error loading businesses:", error);
    }
  };

  const loadBranches = async () => {
    try {
      const data = await getBranches({ branch_Active: true });
      setBranches(data || []);
    } catch (error) {
      console.error("Error loading branches:", error);
    }
  };

  const loadProducts = async () => {
    try {
      // Load all products (including inactive) to ensure we can display names for all products in invoices
      // Invoices may contain products that are now inactive
      const data = await getProducts({}); // Remove status filter to get all products
      setProducts(data || []);
    } catch (error) {
      console.error("Error loading products:", error);
    }
  };

  const loadServices = async () => {
    try {
      const data = await getSpaServices({ status: true });
      setServices(data || []);
    } catch (error) {
      console.error("Error loading services:", error);
    }
  };

  const loadPackages = async () => {
    try {
      const data = await getSpaPackages({ status: true });
      setPackages(data || []);
    } catch (error) {
      console.error("Error loading packages:", error);
    }
  };

  const loadPackageSeries = async () => {
    try {
      const data = await getSpaPackageSeries({ status: true });
      setPackageSeries(data || []);
    } catch (error) {
      console.error("Error loading package series:", error);
    }
  };

  const loadEmployees = async () => {
    try {
      const data = await getEmployees({ status: true });
      setEmployees(data || []);
    } catch (error) {
      console.error("Error loading employees:", error);
    }
  };

  const loadInvoices = async () => {
    try {
      setLoading(true);
      const data = await getSalesInvoices(filter);

      // Sort invoices by invoice ID in descending order (newest first)
      const sortedData = (data || []).sort((a, b) => {
        const idA = a.invoice_Id || a.Invoice_Id || 0;
        const idB = b.invoice_Id || b.Invoice_Id || 0;
        return idB - idA;
      });

      setInvoices(sortedData);
    } catch (error) {
      console.error("Error loading invoices:", error);
      Swal.fire("Error", "Failed to load invoices", "error");
    } finally {
      setLoading(false);
    }
  };

  // Manual search function for customer filter
  const searchCustomersFilterManually = async (inputValue) => {
    if (!inputValue || inputValue.length < 3) {
      setCustomerSearchOptions([]);
      return;
    }

    try {
      setCustomerSearchLoading(true);
      const cleanedInput = inputValue.replace(/\s+/g, '');
      const response = await searchCustomers(cleanedInput, 1, 20);

      const formattedOptions = response.items.map(customer => ({
        value: customer.customer_Id,
        label: `${customer.customer_NameEn || 'N/A'} â€” ${customer.customer_ContactNo || 'N/A'}`,
        customer: customer
      }));

      setCustomerSearchOptions(formattedOptions);
    } catch (error) {
      console.error("Error searching customers:", error);
      setCustomerSearchOptions([]);
    } finally {
      setCustomerSearchLoading(false);
    }
  };

  const handleApplyFilter = () => {
    setHasAppliedFilter(true);
  };

  const handleClearFilter = () => {
    setFilter(emptyFilter);
    setCustomerSearchOptions([]);
    setSelectedCustomerFilterOption(null);
    setHasAppliedFilter(true);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  // Helper functions
  const getCustomerName = (customerId) => {
    if (!customerId) return "N/A";
    const customer = customers.find(c => c.customer_Id === customerId);
    if (!customer) return "N/A";
    const customerName = customer.customer_NameEn || "N/A";
    const phoneNumber = customer.customer_ContactNo || "";
    return phoneNumber ? `${customerName} - ${phoneNumber}` : customerName;
  };

  const getBusinessName = (businessId) => {
    const business = businesses.find(b => b.business_Id === businessId);
    return business ? business.business_NameEn : "N/A";
  };

  const getBranchName = (branchId) => {
    if (!branchId) return "N/A";
    const branch = branches.find(b => b.branch_Id === branchId);
    return branch ? branch.branch_NameEn : "N/A";
  };

  const getPaymentMethodName = (method) => {
    if (!method) return "N/A";
    const paymentMethodOptions = [
      { value: "ca", label: "Cash" },
      { value: "cc", label: "Credit Card" },
      { value: "ba", label: "Bank Transfer" },
      { value: "ta", label: "Tabee" },
      { value: "on", label: "Online" }
    ];
    const option = paymentMethodOptions.find(opt => opt.value === method);
    return option ? option.label : method;
  };

  const getInvoiceTypeName = (type) => {
    if (!type) return "N/A";
    switch (type.toLowerCase()) {
      case "cr": return "Credit";
      case "ca": return "Cash";
      default: return type.toUpperCase();
    }
  };

  // Filter invoices based on isReturn flag
  const filteredInvoices = useMemo(() => {
    if (invoiceListType === 'return') {
      return invoices.filter(inv => inv.isReturn === true || inv.IsReturn === true);
    } else {
      return invoices.filter(inv => !inv.isReturn && !inv.IsReturn);
    }
  }, [invoices, invoiceListType]);

  // Calculate pagination based on filtered invoices
  const filteredTotalCount = filteredInvoices.length;
  const totalPages = Math.ceil(filteredTotalCount / pageSize);
  const startIndex = (page - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedData = filteredInvoices.slice(startIndex, endIndex);

  // Reset to page 1 when switching between invoice types
  useEffect(() => {
    setPage(1);
  }, [invoiceListType]);

  // Options for dropdowns
  const invoiceTypeOptions = [
    { value: "ca", label: "Cash" },
    { value: "cr", label: "Credit" }
  ];

  const paymentMethodOptions = [
    { value: "ca", label: "Cash" },
    { value: "cc", label: "Credit Card" },
    { value: "ba", label: "Bank Transfer" },
    { value: "ta", label: "Tabee" },
    { value: "on", label: "Online" }
  ];

  const handleView = async (item) => {
    try {
      // Ensure products are loaded before displaying modal
      if (products.length === 0) {
        await loadProducts();
      }
      // Also ensure other entities are loaded
      if (services.length === 0) {
        await loadServices();
      }
      if (packages.length === 0) {
        await loadPackages();
      }
      if (packageSeries.length === 0) {
        await loadPackageSeries();
      }
      if (employees.length === 0) {
        await loadEmployees();
      }

      // Format date
      const invoiceDate = item.invoice_Date || item.Invoice_Date;

      // Format amounts
      const totalAmount = (item.invoice_TotalAmount || item.Invoice_TotalAmount || 0).toFixed(2);
      const discountAmount = (item.invoice_DisAmount || item.Invoice_DisAmount || 0).toFixed(2);
      const taxAmount = (item.invoice_TaxAmount || item.Invoice_TaxAmount || 0).toFixed(2);
      const netAmount = (item.invoice_NetAmount || item.Invoice_NetAmount || 0).toFixed(2);
      const paidAmount = (item.paidAmount || item.PaidAmount || 0).toFixed(2);
      const tipAmount = (item.tipAmount || item.TipAmount || 0).toFixed(2);
      const cashAmount = (item.cashAmount || item.CashAmount || 0).toFixed(2);
      const cashlessAmount = (item.cashlessAmount || item.CashlessAmount || 0).toFixed(2);

      // Get business name
      const businessName = getBusinessName(item.invoice_BusinessId || item.Invoice_BusinessId);

      // Get branch name
      const branchName = getBranchName(item.invoice_BranchId || item.Invoice_BranchId);

      // Helper function to get entity name
      const getEntityName = (entityType, entityId) => {
        if (!entityId) return 'N/A';

        // Convert entityId to number for comparison
        const entityIdNum = parseInt(entityId, 10);
        if (isNaN(entityIdNum) || entityIdNum <= 0) return `Entity ID: ${entityId}`;

        if (entityType === 'pro') {
          // Try to find product with multiple property name variations
          const product = products.find(p => {
            const pId = parseInt(p.product_Id || p.productId || p.id || p.value || 0, 10);
            return pId === entityIdNum && pId > 0;
          });

          if (product) {
            return product.product_NameEn || product.productName_En || product.name || product.label || `Product ID: ${entityId}`;
          }

          // If not found, log for debugging
          console.warn(`Product not found in products array. EntityId: ${entityId}, Products loaded: ${products.length}, Product IDs in array:`, products.map(p => p.product_Id || p.productId || p.id).slice(0, 10));
          return `Product ID: ${entityId}`;
        } else if (entityType === 'ser') {
          const service = services.find(s => {
            const sId = parseInt(s.spaService_Id || s.spaServiceId || s.id || s.value || 0, 10);
            return sId === entityIdNum && sId > 0;
          });
          return service ? (service.spaServiceName_En || service.spaServiceNameEn || service.name || service.label || `Service ID: ${entityId}`) : `Service ID: ${entityId}`;
        } else if (entityType === 'pac') {
          const pkg = packages.find(p => {
            const pId = parseInt(p.packageId || p.package_Id || p.id || p.value || 0, 10);
            return pId === entityIdNum && pId > 0;
          });
          return pkg ? (pkg.packageName_En || pkg.packageNameEn || pkg.name || pkg.label || `Package ID: ${entityId}`) : `Package ID: ${entityId}`;
        } else if (entityType === 'sep') {
          const series = packageSeries.find(s => {
            const sId = parseInt(s.seriesId || s.series_Id || s.id || s.value || 0, 10);
            return sId === entityIdNum && sId > 0;
          });
          return series ? (series.seriesName_En || series.seriesNameEn || series.name || series.label || `Package Series ID: ${entityId}`) : `Package Series ID: ${entityId}`;
        }
        return `Entity ID: ${entityId}`;
      };

      // Helper function to get salesman name
      const getSalesmanName = (salesmanId) => {
        if (!salesmanId || salesmanId === 0) return 'N/A';
        const employee = employees.find(emp => emp.employee_Id === salesmanId);
        return employee ? employee.employee_NameEn : `Employee ID: ${salesmanId}`;
      };

      // Separate details by type
      let productsHtml = '';
      let servicesHtml = '';
      let packagesHtml = '';
      let packageSeriesHtml = '';

      if (item.details && item.details.length > 0) {
        const productsList = item.details.filter(detail => detail.entityType === 'pro');
        const servicesList = item.details.filter(detail => detail.entityType === 'ser');
        const packagesList = item.details.filter(detail => detail.entityType === 'pac');
        const packageSeriesList = item.details.filter(detail => detail.entityType === 'sep');

        // Products section
        if (productsList.length > 0) {
          productsHtml = `
            <div class="mt-3">
              <h6 class="text-success">Products</h6>
              <div class="table-responsive">
                <table class="table table-sm table-bordered">
                  <thead class="table-light">
                    <tr>
                      <th>Product Name</th>
                      <th>Quantity</th>
                      <th>Price</th>
                      <th>Discount</th>
                      <th>Tax</th>
                      <th>Salesman</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${productsList.map(detail => `
                      <tr>
                        <td>${getEntityName('pro', detail.entityId)}</td>
                        <td>${detail.quantity}</td>
                        <td>${(detail.price || 0).toFixed(2)}</td>
                        <td>${(detail.disAmount || 0).toFixed(2)}</td>
                        <td>${(detail.taxAmt || 0).toFixed(2)}</td>
                        <td>${getSalesmanName(detail.salesman_ID || detail.Salesman_ID)}</td>
                      </tr>
                    `).join('')}
                  </tbody>
                </table>
              </div>
            </div>
          `;
        }

        // Services section
        if (servicesList.length > 0) {
          servicesHtml = `
            <div class="mt-3">
              <h6 class="text-info">Services</h6>
              <div class="table-responsive">
                <table class="table table-sm table-bordered">
                  <thead class="table-light">
                    <tr>
                      <th>Service Name</th>
                      <th>Quantity</th>
                      <th>Price</th>
                      <th>Discount</th>
                      <th>Tax</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${servicesList.map(detail => `
                      <tr>
                        <td>${getEntityName('ser', detail.entityId)}</td>
                        <td>${detail.quantity}</td>
                        <td>${(detail.price || 0).toFixed(2)}</td>
                        <td>${(detail.disAmount || 0).toFixed(2)}</td>
                        <td>${(detail.taxAmt || 0).toFixed(2)}</td>
                      </tr>
                    `).join('')}
                  </tbody>
                </table>
              </div>
            </div>
          `;
        }

        // Packages section
        if (packagesList.length > 0) {
          packagesHtml = `
            <div class="mt-3">
              <h6 class="text-warning">Packages</h6>
              <div class="table-responsive">
                <table class="table table-sm table-bordered">
                  <thead class="table-light">
                    <tr>
                      <th>Package Name</th>
                      <th>Quantity</th>
                      <th>Price</th>
                      <th>Discount</th>
                      <th>Tax</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${packagesList.map(detail => `
                      <tr>
                        <td>${getEntityName('pac', detail.entityId)}</td>
                        <td>${detail.quantity}</td>
                        <td>${(detail.price || 0).toFixed(2)}</td>
                        <td>${(detail.disAmount || 0).toFixed(2)}</td>
                        <td>${(detail.taxAmt || 0).toFixed(2)}</td>
                      </tr>
                    `).join('')}
                  </tbody>
                </table>
              </div>
            </div>
          `;
        }

        // Package Series section
        if (packageSeriesList.length > 0) {
          packageSeriesHtml = `
            <div class="mt-3">
              <h6 class="text-primary">Package Series</h6>
              <div class="table-responsive">
                <table class="table table-sm table-bordered">
                  <thead class="table-light">
                    <tr>
                      <th>Package Series Name</th>
                      <th>Quantity</th>
                      <th>Price</th>
                      <th>Discount</th>
                      <th>Tax</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${packageSeriesList.map(detail => `
                      <tr>
                        <td>${getEntityName('sep', detail.entityId)}</td>
                        <td>${detail.quantity}</td>
                        <td>${(detail.price || 0).toFixed(2)}</td>
                        <td>${(detail.disAmount || 0).toFixed(2)}</td>
                        <td>${(detail.taxAmt || 0).toFixed(2)}</td>
                      </tr>
                    `).join('')}
                  </tbody>
                </table>
              </div>
            </div>
          `;
        }
      }

      Swal.fire({
        title: `Invoice ${(item.isReturn || item.IsReturn) ? '' : 'INV-'}${item.invoice_No || item.Invoice_No || 'N/A'}`,
        html: `
          <div class="text-start">
            <div class="row">
              <div class="col-md-6">
                <h6 class="text-primary">Basic Information</h6>
                <p><strong>Invoice Number:</strong> ${(item.isReturn || item.IsReturn) ? '' : 'INV-'}${item.invoice_No || item.Invoice_No || 'N/A'}</p>
                <p><strong>Invoice Date:</strong> ${invoiceDate ? new Date(invoiceDate).toLocaleDateString() : 'N/A'}</p>
                <p><strong>Customer:</strong> ${getCustomerName(item.invoice_CustomerId || item.Invoice_CustomerId)}</p>
                <p><strong>Business:</strong> ${businessName}</p>
                <p><strong>Branch:</strong> ${branchName}</p>
                <p><strong>Source:</strong> ${item.invoice_SourceName || item.Invoice_SourceName || 'N/A'}</p>
                 <p><strong>Campaign:</strong> ${item.invoice_CampaignName || item.Invoice_CampaignName || 'N/A'}</p>
                <p><strong>Invoice Type:</strong> ${getInvoiceTypeName(item.invoice_Type || item.Invoice_Type)}</p>
              </div>
              <div class="col-md-6">
                <h6 class="text-primary">Financial Summary</h6>
                <p><strong>Total Quantity:</strong> ${item.invoice_TotalQty || item.Invoice_TotalQty || 0}</p>
                <p><strong>Total Amount:</strong> ${totalAmount}</p>
                <p><strong>Discount Amount:</strong> ${discountAmount}</p>
                <p><strong>Tax Amount:</strong> ${taxAmount}</p>
                <p><strong>Net Amount:</strong> <span class="fw-bold text-success">${netAmount}</span></p>
                <p><strong>Payment Method:</strong> ${getPaymentMethodName(item.paymentMethod || item.PaymentMethod)}</p>
              </div>
            </div>
            
            <div class="row mt-3">
              <div class="col-md-6">
                <h6 class="text-primary">Payment Details</h6>
                <p><strong>Paid Amount:</strong> ${paidAmount}</p>
                ${parseFloat(tipAmount) > 0 ? `<p><strong>Tip Amount:</strong> ${tipAmount}</p>` : ''}
                <p><strong>Cash Amount:</strong> ${cashAmount}</p>
                <p><strong>Cashless Amount:</strong> ${cashlessAmount}</p>
                <p><strong>Auth Code:</strong> ${item.authCode || item.AuthCode || 'N/A'}</p>
              </div>
            </div>
            
            ${productsHtml}
            ${servicesHtml}
            ${packagesHtml}
            ${packageSeriesHtml}
          </div>
        `,
        icon: "info",
        width: '90%',
        confirmButtonText: 'Close',
        showCloseButton: true,
        customClass: {
          popup: 'swal-wide'
        }
      });
    } catch (error) {
      console.error("Error viewing invoice:", error);
    }
  };

  const handlePrintInvoice = async (item) => {
    try {
      setPrintLoading(true);
      const invoiceId = item.invoice_Id || item.Invoice_Id;

      // Fetch invoice data for print
      const invoiceData = await getInvoiceForPrint(invoiceId);

      if (!invoiceData || !invoiceData.header) {
        Swal.fire("Error", "Invoice not found or unable to load invoice data", "error");
        setPrintLoading(false);
        return;
      }

      setInvoicePrintData(invoiceData);
      setShowInvoicePrintModal(true);
      setPrintLoading(false);
    } catch (error) {
      console.error("Error loading invoice for print:", error);
      Swal.fire("Error", `Failed to load invoice: ${error.response?.data?.message || error.message || "Unknown error"}`, "error");
      setPrintLoading(false);
    }
  };

  const handleSalesReturn = async (invoice) => {
    try {
      setLoading(true);

      // Show loading message
      Swal.fire({
        title: 'Loading Invoice Data',
        text: 'Please wait while we load the invoice details for return...',
        allowOutsideClick: false,
        allowEscapeKey: false,
        showConfirmButton: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      const invoiceData = await getSalesInvoiceForReturn(invoice.invoice_Id || invoice.Invoice_Id);

      // Close loading message
      Swal.close();

      // Handle different response structures
      let invoiceDataToUse = invoiceData;
      if (invoiceData && typeof invoiceData === 'object') {
        if (invoiceData.data) {
          invoiceDataToUse = invoiceData.data;
        }
        if (Array.isArray(invoiceData) && invoiceData.length > 0) {
          invoiceDataToUse = invoiceData[0];
        }
      }

      if (!invoiceDataToUse || (!invoiceDataToUse.invoice_Id && !invoiceDataToUse.Invoice_Id)) {
        Swal.fire("Error", "Failed to load invoice for return: Invalid data received", "error");
        setLoading(false);
        return;
      }

      // Check if there are returnable items:
      // - Products require available stock details
      // - Services/packages/package-series don't require stock details
      const details = invoiceDataToUse.details || invoiceDataToUse.Details || [];
      const hasProductsWithStock = details.some(detail => {
        const entityType = detail.entityType || detail.EntityType;
        const stockDetails = detail.stockDetails || detail.StockDetails || [];
        return entityType === 'pro' && stockDetails && stockDetails.length > 0;
      });
      const hasNonProductItems = details.some(detail => {
        const entityType = (detail.entityType || detail.EntityType || '').toLowerCase();
        return entityType === 'ser' || entityType === 'pac' || entityType === 'sep';
      });

      if (!hasProductsWithStock && !hasNonProductItems) {
        Swal.fire("Info", "No returnable items found in this invoice.", "info");
        setLoading(false);
        return;
      }

      // Navigate to Billing page with return mode
      navigate('/GmsBilling', {
        state: {
          salesReturnInvoice: invoice,
          salesReturnInvoiceData: invoiceDataToUse
        }
      });
    } catch (error) {
      console.error("Error loading invoice for return:", error);

      // Close any open loading dialogs
      Swal.close();

      // Check for timeout errors
      const isTimeoutError = error.code === 'ECONNABORTED' ||
        error.message?.toLowerCase().includes('timeout') ||
        error.response?.status === 500 ||
        error.response?.data?.message?.toLowerCase().includes('timeout');

      if (isTimeoutError) {
        Swal.fire({
          icon: "error",
          title: "Request Timeout",
          html: `The request to load invoice data is taking too long. This might be due to:<br/><br/>
                 â€¢ Large invoice with many items<br/>
                 â€¢ Database performance issues<br/>
                 â€¢ Network connectivity problems<br/><br/>
                 <strong>Please try again later or contact your system administrator.</strong>`,
          confirmButtonText: "OK"
        });
      } else {
        Swal.fire({
          icon: "error",
          title: "Error Loading Invoice",
          text: error.response?.data?.message || error.message || "Failed to load invoice for return. Please try again.",
          confirmButtonText: "OK"
        });
      }

      setLoading(false);
    }
  };

  const handleAddNewInvoice = () => {
    navigate('/GmsBilling');
  };

  const InvoicePrintModal = ({ isOpen, toggle, invoiceData, businesses = [] }) => {
    if (!invoiceData || !invoiceData.header) return null;

    const printRef = useRef(null);

    const header = invoiceData.header;
    const details = invoiceData.details || [];
    const taxes = invoiceData.taxes || [];

    // Extract values with fallback for camelCase/PascalCase
    const invoiceNo = header.invoice_No || header.Invoice_No;
    const invoiceDate = header.invoice_Date || header.Invoice_Date;
    const customerName = header.customer_NameEn || header.Customer_NameEn;
    const customerAddress = header.customer_AddressEn || header.Customer_AddressEn;
    const customerTRN = header.customer_TRN || header.Customer_TRN;
    const customerContactNo = header.customer_ContactNo || header.Customer_ContactNo;
    const businessName = header.business_NameEn || header.Business_NameEn;
    const businessTRN = header.business_TRN || header.Business_TRN;
    const companyLogoPath = header.company_LogoPath || header.Company_LogoPath;

    const invoiceBusinessId = header.invoice_BusinessId || header.Invoice_BusinessId;
    const business = (businesses || []).find(b => b.business_Id === invoiceBusinessId);
    const businessLogoPath = business?.business_LogoPath || null;
    const businessAddress = business?.business_Address || business?.business_AddressEn || header.business_AddressEn || header.Business_AddressEn || '';
    const logoPath = businessLogoPath || companyLogoPath;
    const overallDiscount = header.overallDiscountAmount || header.OverallDiscountAmount || 0;
    const grandTotal = header.invoice_NetAmount || header.Invoice_NetAmount || 0;
    const tipAmount = header.tipAmount || header.TipAmount || 0;
    const pointsAmount = header.pointsAmount || header.PointsAmount || 0;
    const giftCardAmount = header.giftCardAmount || header.GiftCardAmount || 0;
    
    const formatAmount = (amount) => {
      return (amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    const formatDate = (dateString) => {
      if (!dateString) return 'N/A';
      try {
        const date = new Date(dateString);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const seconds = String(date.getSeconds()).padStart(2, '0');
        return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
      } catch (error) {
        return 'N/A';
      }
    };

    const getInvoiceTypeLabel = (type) => {
      if (!type) return 'N/A';
      const lowType = type.toString().toLowerCase();
      if (lowType === 'ca') return 'Cash';
      if (lowType === 'cr') return 'Credit';
      return type;
    };

    const invoiceTypeLabel = getInvoiceTypeLabel(header.invoice_TypeEn || header.Invoice_TypeEn || header.invoice_Type || header.Invoice_Type);

    const handlePrint = () => {
      if (!printRef.current) return;
      const element = printRef.current;
      const opt = {
        margin:       [10, 10, 10, 10],
        filename:     `Invoice - ${invoiceNo}${Date.now()}.pdf`,
        image:        { type: 'jpeg', quality: 0.98 },
        html2canvas:  { scale: 2, useCORS: true, logging: false },
        jsPDF:        { unit: 'mm', format: 'a4', orientation: 'portrait' },
        pagebreak:    { mode: ['css', 'legacy'] }
      };
      html2pdf().set(opt).from(element).save();
    };

    const ITEMS_PER_PAGE = 8;
    const pagesCount = Math.max(1, Math.ceil((details.length || 0) / ITEMS_PER_PAGE));
    
    const renderHeader = (hideBorderBottom = false) => (
        <React.Fragment>
            <tr>
              <th colSpan="8" style={{ border: 'none', padding: '0 0 20px 0' }}>
                <div className="invoice-header-bg">
                  <div style={{ textAlign: 'center', marginBottom: '15px' }}>
                    <h3 style={{ fontSize: '24px', margin: '0', color: 'black', fontWeight: 'bold', letterSpacing: '1px' }}>TAX INVOICE</h3>
                  </div>
                   <table style={{ width: '100%', border: 'none', borderCollapse: 'collapse' }}>
                    <tbody>
                      <tr>
                        {logoPath && (
                          <td style={{ width: '120px', border: 'none', padding: '0 20px 0 0', verticalAlign: 'middle' }}>
                            <img
                              src={`${BASE_URL}${logoPath.startsWith('/') ? logoPath : '/' + logoPath}`}
                              alt="Business Logo"
                              style={{ maxHeight: '90px', maxWidth: '110px', objectFit: 'contain' }}
                              crossOrigin="anonymous"
                              onError={(e) => { e.target.style.display = 'none'; }}
                            />
                          </td>
                        )}
                        <td style={{ border: 'none', verticalAlign: 'middle', textAlign: 'left' }}>
                          <h4 className="mb-1" style={{ fontSize: '20px', color: 'black', fontWeight: '800' }}>{(businessName || 'COMPANY').toUpperCase()}</h4>
                          {businessAddress && <p className="mb-2" style={{ color: 'black', fontSize: '13px', fontWeight: '600', lineHeight: '1.4', maxWidth: '500px' }}>{businessAddress}</p>}
                          {businessTRN && businessTRN.trim() !== '' && (
                            <div className="trn-badge">TRN: {businessTRN}</div>
                          )}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </th>
            </tr>
            <tr>
              <th colSpan="8" style={{ border: 'none', padding: '0 0 20px 0' }}>
                <table style={{ width: '100%', border: 'none', borderCollapse: 'collapse' }}>
                  <tbody>
                    <tr>
                      <td style={{ width: '50%', border: 'none', padding: '0 8px', verticalAlign: 'top', textAlign: 'left' }}>
                        <p className="mb-1" style={{color: 'black', fontSize: '13px'}}><strong>Invoice Date:</strong> {formatDate(invoiceDate)}</p>
                        <p className="mb-1" style={{color: 'black', fontSize: '13px'}}><strong>Invoice No:</strong> {(header.isReturn || header.IsReturn) ? '' : 'INV-'}{invoiceNo}</p>
                        <p className="mb-1" style={{color: 'black', fontSize: '13px'}}><strong>Invoice Type:</strong> {invoiceTypeLabel}</p>
                      </td>
                      <td style={{ width: '50%', border: 'none', padding: '0 8px', verticalAlign: 'top', textAlign: 'left' }}>
                        <p className="mb-1" style={{color: 'black', fontSize: '13px'}}><strong>Customer Name:</strong> {customerName || 'N/A'}{customerContactNo && customerContactNo.trim() !== '' && ` (${customerContactNo})`}</p>
                        {customerAddress && customerAddress.trim() !== '' && (
                          <p className="mb-1" style={{color: 'black', fontSize: '13px'}}><strong>Address:</strong> {customerAddress}</p>
                        )}
                        {customerTRN && customerTRN.trim() !== '' && (
                          <p className="mb-1" style={{color: 'black', fontSize: '13px'}}><strong>Customer TRN:</strong> {customerTRN}</p>
                        )}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </th>
            </tr>
            <tr className="invoice-table-header-row">
              <th style={{color: 'black'}}>S.No / الرقم</th>
              <th style={{color: 'black'}}>Description / الوصف</th>
              <th className="text-end" style={{color: 'black'}}>Price / السعر</th>
              <th className="text-end" style={{color: 'black'}}>Qty / الكمية</th>
              <th className="text-end" style={{color: 'black'}}>Disc. / الخصم</th>
              <th className="text-end" style={{color: 'black'}}>Taxable Amount / المبلغ الخاضع للضريبة</th>
              <th className="text-end" style={{color: 'black'}}>VAT / ضريبة القيمة المضافة</th>
              <th className="text-end" style={{color: 'black'}}>Total / الإجمالي</th>
            </tr>
        </React.Fragment>
    );

    const renderTotals = () => (
        <React.Fragment>
            <tr>
              <td colSpan="6" rowSpan={2 + (taxes?.length || 0) + (overallDiscount > 0 ? 1 : 0) + (tipAmount > 0 ? 1 : 0) + (pointsAmount > 0 ? 1 : 0) + (giftCardAmount > 0 ? 1 : 0)} style={{ border: 'none' }}></td>
              <td className="text-end" style={{color: 'black'}}><strong>Subtotal (AED):</strong></td>
              <td className="text-end" style={{color: 'black'}}><strong>{formatAmount(details.reduce((sum, item) => sum + ((item.netAmount || item.NetAmount || 0) - (item.taxAmt || item.TaxAmt || 0)), 0))}</strong></td>
            </tr>
            {taxes && taxes.length > 0 && (
              (() => {
                const taxTotals = {};
                taxes.forEach(tax => {
                  const name = tax.tax_NameEn || tax.Tax_NameEn || 'Tax';
                  const perc = tax.tax_Perc || tax.Tax_Perc || 5;
                  const key = `${name}_${perc}`;
                  if (!taxTotals[key]) taxTotals[key] = { name, perc, amount: 0 };
                });
                details.forEach(item => {
                  const itemTax = item.taxAmt || item.TaxAmt || 0;
                  const firstTaxKey = Object.keys(taxTotals)[0];
                  if (firstTaxKey) taxTotals[firstTaxKey].amount += itemTax;
                });
                return Object.values(taxTotals).map(tax => (
                  <tr key={`${tax.name}_${tax.perc}`}>
                    <td className="text-end" style={{color: 'black'}}><strong>{tax.name} ({tax.perc}%) (AED):</strong></td>
                    <td className="text-end" style={{color: 'black'}}><strong>{formatAmount(tax.amount)}</strong></td>
                  </tr>
                ));
              })()
            )}
            {overallDiscount > 0 && (
              <tr>
                <td className="text-end" style={{color: 'black'}}><strong>Discount (AED):</strong></td>
                <td className="text-end" style={{color: 'black'}}><strong>{formatAmount(overallDiscount)}</strong></td>
              </tr>
            )}
            {tipAmount > 0 && (
              <tr>
                <td className="text-end" style={{color: 'black'}}><strong>Tip (AED):</strong></td>
                <td className="text-end" style={{color: 'black'}}><strong>{formatAmount(tipAmount)}</strong></td>
              </tr>
            )}
            {pointsAmount > 0 && (
              <tr>
                <td className="text-end" style={{color: 'black'}}><strong>Loyalty (AED):</strong></td>
                <td className="text-end" style={{color: 'black'}}><strong>- {formatAmount(pointsAmount)}</strong></td>
              </tr>
            )}
            {giftCardAmount > 0 && (
              <tr>
                <td className="text-end" style={{color: 'black'}}><strong>Gift Card (AED):</strong></td>
                <td className="text-end" style={{color: 'black'}}><strong>- {formatAmount(giftCardAmount)}</strong></td>
              </tr>
            )}
            <tr style={{ backgroundColor: '#ebf2f8' }}>
              <td className="text-end" style={{color: 'black'}}><strong>Grand Total (AED):</strong></td>
              <td className="text-end" style={{color: 'black'}}><strong>{formatAmount(grandTotal)}</strong></td>
            </tr>
        </React.Fragment>
    );

    const renderTerms = () => (
      <tfoot>
        <tr>
          <td colSpan="8" style={{ border: 'none', padding: 0 }}>
            <div className="row mt-4 mb-4" style={{ pageBreakInside: 'avoid' }}>
              <div className="col-12">
                <div className="border border-secondary rounded p-3" style={{ backgroundColor: '#fdfdfd' }}>
                  <h6 style={{ fontSize: '14px', fontWeight: 'bold', color: '#5d4037' }}>Terms & Notes / الشروط والملاحظات:</h6>
                  <ul className="ps-3 mb-0" style={{ fontSize: '12px', listStyleType: 'disc', color: 'black' }}>
                    <li>Discounts are applied per service as shown / يتم تطبيق الخصومات لكل خدمة كما هو موضح</li>
                    <li>VAT charged as per UAE regulations / يتم احتساب ضريبة القيمة المضافة حسب لوائح الإمارات العربية المتحدة</li>
                    <li>No refund after service completion / لا يوجد استرداد بعد اكتمال الخدمة</li>
                    <li>This is a computer-generated invoice and does not require a signature / هذه فاتورة تم إنشاؤها بواسطة الكمبيوتر ولا تتطلب توقيعًا</li>
                  </ul>
                </div>
              </div>
            </div>
          </td>
        </tr>
      </tfoot>
    );

    let currentItemIndex = 0;
    const pagesList = [];
    
    for (let i = 0; i < pagesCount; i++) {
        const isLastRowPage = (i === pagesCount - 1);
        const pageItems = details.slice(i * ITEMS_PER_PAGE, (i + 1) * ITEMS_PER_PAGE);
        const requiresExtraTotalsPage = isLastRowPage && pageItems.length > 5;
        
        const showTotalsAndTermsOnThisPage = isLastRowPage && !requiresExtraTotalsPage;
        
        pagesList.push(
            <div key={`page-${i}`} style={{ pageBreakAfter: (showTotalsAndTermsOnThisPage && !requiresExtraTotalsPage) ? 'auto' : 'always', marginBottom: '20px' }}>
                <table className="table invoice-table" style={{ width: '100%', borderCollapse: 'collapse', color: 'black' }}>
                    <thead>{renderHeader()}</thead>
                    <tbody>
                        {pageItems.map((detail, index) => {
                             const priceWithTax = detail.netAmount || detail.NetAmount || 0;
                             const taxAmt = detail.taxAmt || detail.TaxAmt || 0;
                             const taxableAmount = priceWithTax - taxAmt;
                             const originalPrice = detail.price || detail.Price || 0;
                             
                             return (
                                <tr key={index}>
                                  <td style={{color: 'black'}}>{currentItemIndex + index + 1}</td>
                                  <td style={{color: 'black'}}>
                                    {detail.entityName || detail.EntityName}
                                    {(detail.entityType === 'gif' || detail.EntityType === 'gif') && detail.stockData && ` (${detail.stockData})`}
                                  </td>
                                  <td className="text-end" style={{color: 'black'}}>{formatAmount(originalPrice)}</td>
                                  <td className="text-end" style={{color: 'black'}}>{Math.round(detail.quantity || detail.Quantity || 0)}</td>
                                  <td className="text-end" style={{color: 'black'}}>{formatAmount(detail.disAmount || detail.DisAmount || 0)}</td>
                                  <td className="text-end" style={{color: 'black'}}>{formatAmount(taxableAmount)}</td>
                                  <td className="text-end" style={{color: 'black'}}>{formatAmount(taxAmt)}</td>
                                  <td className="text-end" style={{color: 'black'}}>{formatAmount(priceWithTax)}</td>
                                </tr>
                             );
                        })}
                        {showTotalsAndTermsOnThisPage && renderTotals()}
                    </tbody>
                    {showTotalsAndTermsOnThisPage && renderTerms()}
                </table>
            </div>
        );
        currentItemIndex += ITEMS_PER_PAGE;
        
        if (requiresExtraTotalsPage) {
             pagesList.push(
                <div key={`page-totals`} style={{ pageBreakAfter: 'auto' }}>
                    <table className="table invoice-table" style={{ width: '100%', borderCollapse: 'collapse', color: 'black' }}>
                        <thead>{renderHeader(true)}</thead>
                        <tbody>
                            <tr><td colSpan="8" style={{ border: 'none', padding: '10px 0' }}></td></tr>
                            {renderTotals()}
                        </tbody>
                        {renderTerms()}
                    </table>
                </div>
            );
        }
    }

    return (
      <Modal isOpen={isOpen} toggle={toggle} size="lg">
        <style>
          {`
            .invoice-print-content, 
            .invoice-print-content * {
              color: black !important;
              font-family: 'Inter', sans-serif !important;
            }
            .invoice-header-bg {
              background-color: #ebf2f8 !important;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
              border-radius: 12px;
              padding: 20px;
              margin-bottom: 25px;
              border: 1px solid #d6e4f0;
            }
            .trn-badge {
              background-color: #d6e4f0 !important;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
              padding: 4px 15px;
              border-radius: 6px;
              font-weight: bold;
              display: inline-block;
              border: 1px solid #c1d5e6;
              color: #2c3e50;
              font-size: 13px;
            }
            .invoice-table {
              margin-bottom: 0 !important;
              border-collapse: collapse !important;
              border: none !important;
            }
            .invoice-table th {
              background-color: #f0f5fa !important;
              border: 1px solid #dee2e6 !important;
              padding: 12px 10px !important;
              font-weight: 700 !important;
              font-size: 13px !important;
              text-align: left;
            }
            .invoice-table-header-row th:first-child {
              border-top-left-radius: 12px !important;
            }
            .invoice-table-header-row th:last-child {
              border-top-right-radius: 12px !important;
            }
            .invoice-table td {
              border: 1px solid #dee2e6 !important;
              padding: 10px !important;
              font-size: 13px !important;
            }
            .invoice-table .text-end {
              text-align: right !important;
            }
          `}
        </style>
        <ModalHeader toggle={toggle}>
          Invoice - {(header.isReturn || header.IsReturn) ? '' : 'INV-'}{header.invoice_No || header.Invoice_No}
        </ModalHeader>
        <ModalBody>
          <div ref={printRef} className="p-4 invoice-print-content" style={{ color: 'black' }}>
            {pagesList}
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={handlePrint} className="waves-effect waves-light d-flex align-items-center">
            <Printer size={16} className="me-2" />
            Print
          </Button>
          <Button color="secondary" onClick={toggle} className="waves-effect waves-light">
            Close
          </Button>
        </ModalFooter>
      </Modal>
    );
  };

  if (!hasPermission) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="text-center">
            <h4>Access Denied</h4>
            <p>You don't have permission to access this page.</p>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumb title="Invoice List" breadcrumbItem="Sales" />

        <Row>
          <Col lg={12}>
            <Card>
              <CardBody>
                <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
                  <h4 className="card-title mb-0">Invoice List</h4>
                  <div className="d-flex flex-wrap align-items-center gap-2">
                    <Button
                      color="secondary"
                      outline
                      onClick={() => setShowFilters(!showFilters)}
                      className="waves-effect waves-light"
                      style={{ padding: '6px 12px', height: '34px' }}
                    >
                      {showFilters ? "Hide Filters" : "Show Filters"}
                    </Button>
                    {hasEditOrHigher && (
                      <Button
                        color="primary"
                        onClick={handleAddNewInvoice}
                        className="waves-effect waves-light"
                        style={{ padding: '8px 14px', height: '36px' }}
                      >
                        <i className="bx bx-plus me-1"></i>
                        Add New Invoice
                      </Button>
                    )}
                  </div>
                </div>

                {showFilters && (
                  <Card className="mb-3">
                    <CardBody>
                      <Row className="g-3">
                        <Col md={3}>
                          <Label for="filterInvoiceId">Invoice Number</Label>
                          <Input
                            id="filterInvoiceId"
                            name="filterInvoiceId"
                            type="number"
                            value={filter.Invoice_Id || ""}
                            onChange={(e) => setFilter({ ...filter, Invoice_Id: e.target.value ? parseInt(e.target.value) : null })}
                            placeholder="Search invoice number..."
                          />
                        </Col>
                        <Col md={3}>
                          <Label for="filterCustomer">Customer Name</Label>
                          <div className="d-flex gap-2">
                            <div style={{ flex: 1 }}>
                              <Select
                                value={selectedCustomerFilterOption}
                                onChange={(selected) => {
                                  setSelectedCustomerFilterOption(selected);
                                  setFilter({ ...filter, Customer_Id: selected?.value || null });
                                }}
                                options={customerSearchOptions}
                                placeholder="Type at least 3 characters to search customers..."
                                styles={selectStyles}
                                isClearable
                                isSearchable
                                filterOption={() => true}
                                isLoading={customerSearchLoading}
                                noOptionsMessage={() => "Type at least 3 characters to search"}
                                loadingMessage={() => "Searching customers..."}
                                onInputChange={(inputValue) => {
                                  if (searchTimeout) {
                                    clearTimeout(searchTimeout);
                                  }
                                  const timeout = setTimeout(() => {
                                    searchCustomersFilterManually(inputValue);
                                  }, 300);
                                  setSearchTimeout(timeout);
                                  return inputValue;
                                }}
                              />
                            </div>
                            <Button
                              color="info"
                              size="sm"
                              onClick={() => setShowAdvancedSearchModal(true)}
                              title="Advanced Customer Search"
                              style={{ height: '38px' }}
                            >
                              <Search size={16} />
                            </Button>
                          </div>
                        </Col>
                        <Col md={3}>
                          <Label for="filterFromDate">From Date</Label>
                          <DatePicker
                            selected={filter.FromDate}
                            onChange={(date) => setFilter({ ...filter, FromDate: date })}
                            className="form-control"
                            placeholderText="Select from date"
                            dateFormat="dd/MM/yyyy"
                            isClearable
                          />
                        </Col>
                        <Col md={3}>
                          <Label for="filterToDate">To Date</Label>
                          <DatePicker
                            selected={filter.ToDate}
                            onChange={(date) => setFilter({ ...filter, ToDate: date })}
                            className="form-control"
                            placeholderText="Select to date"
                            dateFormat="dd/MM/yyyy"
                            isClearable
                          />
                        </Col>
                        <Col md={3}>
                          <Label for="filterInvoiceType">Invoice Type</Label>
                          <Select
                            value={invoiceTypeOptions.find(option => option.value === filter.Invoice_Type)}
                            onChange={(selected) => setFilter({ ...filter, Invoice_Type: selected?.value || null })}
                            options={invoiceTypeOptions}
                            placeholder="Select Invoice Type"
                            styles={selectStyles}
                            isClearable
                          />
                        </Col>
                        <Col md={3}>
                          <Label for="filterPaymentMethod">Payment Method</Label>
                          <Select
                            value={paymentMethodOptions.find(option => option.value === filter.PaymentMethod)}
                            onChange={(selected) => setFilter({ ...filter, PaymentMethod: selected?.value || null })}
                            options={paymentMethodOptions}
                            placeholder="Select Payment Method"
                            styles={selectStyles}
                            isClearable
                          />
                        </Col>
                        <Col md={12} className="d-flex align-items-end">
                          <div className="d-flex gap-2">
                            <Button
                              color="primary"
                              onClick={handleApplyFilter}
                              className="waves-effect waves-light"
                            >
                              Apply Filter
                            </Button>
                            <Button
                              color="secondary"
                              onClick={handleClearFilter}
                              className="waves-effect waves-light"
                            >
                              Clear Filter
                            </Button>
                          </div>
                        </Col>
                      </Row>
                    </CardBody>
                  </Card>
                )}

                {/* Tabs for Sales Invoices and Sales Returns */}
                <Nav tabs className="nav-tabs-custom mb-3">
                  <NavItem>
                    <NavLink
                      style={{
                        cursor: "pointer",
                        fontWeight: invoiceListType === 'normal' ? "bold" : "normal",
                        color: invoiceListType === 'normal' ? "#556ee6" : "#74788d",
                        borderBottom: invoiceListType === 'normal' ? "2px solid #556ee6" : "none",
                        padding: "10px 20px",
                        fontSize: "14px"
                      }}
                      className={invoiceListType === 'normal' ? "active" : ""}
                      onClick={() => setInvoiceListType('normal')}
                    >
                      <Receipt size={16} className="me-2" />
                      Sales Invoices
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink
                      style={{
                        cursor: "pointer",
                        fontWeight: invoiceListType === 'return' ? "bold" : "normal",
                        color: invoiceListType === 'return' ? "#556ee6" : "#74788d",
                        borderBottom: invoiceListType === 'return' ? "2px solid #556ee6" : "none",
                        padding: "10px 20px",
                        fontSize: "14px"
                      }}
                      className={invoiceListType === 'return' ? "active" : ""}
                      onClick={() => setInvoiceListType('return')}
                    >
                      <RotateCcw size={16} className="me-2" />
                      Sales Returns
                    </NavLink>
                  </NavItem>
                </Nav>

                {loading ? (
                  <div className="text-center p-4">
                    <ClipLoader size={50} color="#556ee6" />
                    <p className="mt-2">Loading invoices...</p>
                  </div>
                ) : (
                  <>
                    <div className="table-responsive">
                      <Table className="table table-bordered table-striped table-hover">
                        <thead className="table-light">
                          <tr>
                            <th width="5%">#</th>
                            <th width="12%">Invoice Number</th>
                            <th width="12%">Date</th>
                            <th width="15%">Customer Name</th>
                            <th width="12%">Company</th>
                            <th width="10%">Branch</th>
                            <th width="10%">Invoice Type</th>
                            <th width="10%">Total Quantity</th>
                            <th width="12%">Net Amount</th>
                            <th width="12%">Payment Method</th>
                            <th width="8%">Document</th>
                            <th width="12%">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {paginatedData.length > 0 ? (
                            paginatedData.map((item, index) => {
                              const documentPath = item.invoice_DocumentPath || item.Invoice_DocumentPath || null;
                              return (
                                <tr key={item.invoice_Id || item.Invoice_Id}>
                                  <td>{startIndex + index + 1}</td>
                                  <td>{(item.isReturn || item.IsReturn) ? '' : 'INV-'}{item.invoice_No || item.Invoice_No || 'N/A'}</td>
                                  <td>{item.invoice_Date || item.Invoice_Date ? new Date(item.invoice_Date || item.Invoice_Date).toLocaleDateString('en-GB') : 'N/A'}</td>
                                  <td>
                                    <a
                                      href="#"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        const customerId = item.invoice_CustomerId || item.Invoice_CustomerId;
                                        if (customerId) {
                                          setSelectedCustomerId(customerId);
                                          setShowCustomerProfile(true);
                                        }
                                      }}
                                      style={{ color: '#556ee6', textDecoration: 'none' }}
                                    >
                                      {getCustomerName(item.invoice_CustomerId || item.Invoice_CustomerId)}
                                    </a>
                                  </td>
                                  <td>{getBusinessName(item.invoice_BusinessId || item.Invoice_BusinessId)}</td>
                                  <td>{getBranchName(item.invoice_BranchId || item.Invoice_BranchId)}</td>
                                  <td>{getInvoiceTypeName(item.invoice_Type || item.Invoice_Type)}</td>
                                  <td>{item.invoice_TotalQty || item.Invoice_TotalQty || 0}</td>
                                  <td>{(item.invoice_NetAmount || item.Invoice_NetAmount || 0).toFixed(2)}</td>
                                  <td>{getPaymentMethodName(item.paymentMethod || item.PaymentMethod)}</td>
                                  <td>
                                    {documentPath ? (
                                      <a
                                        href={`${BASE_URL}${documentPath}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="btn btn-sm btn-outline-primary"
                                        title="View Document"
                                      >
                                        <FileText size={16} />
                                      </a>
                                    ) : (
                                      <span className="text-muted">-</span>
                                    )}
                                  </td>
                                  <td>
                                    <div className="d-flex gap-2">
                                      <Button
                                        color="info"
                                        size="sm"
                                        onClick={() => handleView(item)}
                                        className="waves-effect waves-light"
                                        title="View Invoice"
                                      >
                                        <Eye size={12} />
                                      </Button>
                                      <Button
                                        color="success"
                                        size="sm"
                                        onClick={() => handlePrintInvoice(item)}
                                        className="waves-effect waves-light"
                                        title="Print Invoice"
                                        disabled={printLoading}
                                      >
                                        {printLoading ? (
                                          <ClipLoader size={12} color="#ffffff" />
                                        ) : (
                                          <Printer size={12} />
                                        )}
                                      </Button>
                                      {/* Show Return button only for non-return invoices and if user has edit permission */}
                                      {hasEditOrHigher && (!item.isReturn && !item.IsReturn) && (
                                        <Button
                                          color="warning"
                                          size="sm"
                                          onClick={() => handleSalesReturn(item)}
                                          className="waves-effect waves-light"
                                          title="Create Sales Return"
                                        >
                                          <RotateCcw size={12} />
                                        </Button>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              );
                            })
                          ) : (
                            <tr>
                              <td colSpan="12" className="text-center">
                                {hasAppliedFilter
                                  ? "No invoices found matching the filter criteria."
                                  : invoiceListType === 'return'
                                    ? "No sales returns available."
                                    : "No invoices available. Please add some invoices first."}
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </Table>
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && (
                      <div className="d-flex justify-content-between align-items-center mt-3">
                        <div>
                          <p className="text-muted mb-0">
                            Showing {startIndex + 1} to {Math.min(endIndex, filteredTotalCount)} of {filteredTotalCount} entries
                          </p>
                        </div>
                        <div className="pagination-wrapper">
                          <Pagination
                            className="ant-pagination"
                            current={page}
                            total={filteredTotalCount}
                            pageSize={pageSize}
                            onChange={handlePageChange}
                            showTitle={false}
                          />
                        </div>
                      </div>
                    )}
                  </>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* Invoice Print Modal */}
      {invoicePrintData && (
        <InvoicePrintModal
          isOpen={showInvoicePrintModal}
          toggle={() => {
            setShowInvoicePrintModal(false);
            setInvoicePrintData(null);
          }}
          invoiceData={invoicePrintData}
          businesses={businesses}
        />
      )}

      {/* Advanced Customer Search Modal */}
      <CustomerAdvancedSearchModal
        isOpen={showAdvancedSearchModal}
        toggle={() => setShowAdvancedSearchModal(false)}
        userBranchId={session.branchId}
        onCustomerSelected={(customer) => {
          // Set the selected customer in the filter
          setSelectedCustomerFilterOption({
            value: customer.customer_Id,
            label: `${customer.customer_NameEn || 'N/A'} â€” ${customer.customer_ContactNo || 'N/A'}`,
            customer: customer
          });
          setFilter(prev => ({ ...prev, Customer_Id: customer.customer_Id }));
        }}
      />

      {/* Customer Profile Modal */}
      <CustomerProfileModal
        isOpen={showCustomerProfile}
        toggle={() => {
          setShowCustomerProfile(false);
          setSelectedCustomerId(null);
        }}
        customerId={selectedCustomerId}
      />
    </div>
  );
}

